import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const PX_TO_MM = 25.4 / 96;
const SCALE = 2;
const JPEG_QUALITY = 0.92;
// Elementos cujo fundo é um bom ponto para quebrar de página sem cortar texto no meio.
const BREAK_SELECTOR = 'p, h1, h2, h3, h4, h5, h6, li, tr, img, hr, [data-pdf-block]';
const LOCAL_LOGO = '/logo-sem-bg.png';
// Largura de layout usada na captura: o PDF não pode depender do tamanho da janela (ex.: celular).
const CAPTURE_WINDOW_WIDTH = 1280;
const BOTTOM_MARGIN_PX = 24 * SCALE;

interface ExportElementToPdfOptions {
  filename: string;
}

// Faz valer na cópia do documento as regras `@media print` dos componentes (classes `.css-*` do emotion),
// para o PDF sair com o mesmo layout da impressão. As regras globais do index.css ficam de fora (zoom, overflow).
function applyComponentPrintStyles(doc: Document) {
  Array.from(doc.styleSheets).forEach(sheet => {
    let rules: CSSRuleList;

    try {
      rules = sheet.cssRules;
    } catch {
      return; // folha de outra origem
    }

    Array.from(rules).forEach(rule => {
      const media = (rule as CSSMediaRule).media;
      if (!media || !/print/.test(media.mediaText)) return;

      const isComponentRule = Array.from((rule as CSSMediaRule).cssRules).some(inner =>
        (inner as CSSStyleRule).selectorText?.includes('.css-'),
      );

      if (isComponentRule) media.mediaText = 'all';
    });
  });

  const style = doc.createElement('style');
  style.textContent = 'html, body { height: auto !important; overflow: visible !important; }';
  doc.head.appendChild(style);
}

// O html2canvas copia TODO o estilo calculado de cada <svg> da tela como estilo inline, o que "congela" o tamanho
// medido na tela (ex.: width/inline-size) e impede o layout de impressão de redimensioná-lo (faixa do orçamento).
// Sem o estilo inline, valem as regras de CSS do componente.
function stretchSvgs(root: HTMLElement) {
  root.querySelectorAll<SVGElement>('svg[preserveAspectRatio="none"]').forEach(svg => {
    svg.removeAttribute('style');
  });
}

// Imagens de outra origem (ex.: logo no S3) sem CORS "sujam" o canvas e somem; usa a cópia local do logo.
// O html2canvas lê `currentSrc` e as dimensões da imagem na hora; por isso troca o elemento e espera carregar.
async function replaceCrossOriginImages(doc: Document): Promise<void> {
  const loads: Array<Promise<void>> = [];

  doc.querySelectorAll('img').forEach(img => {
    if (!/^https?:\/\//.test(img.src) || img.src.startsWith(window.location.origin)) return;

    const local = img.cloneNode(false) as HTMLImageElement;
    local.removeAttribute('srcset');
    loads.push(
      new Promise<void>(resolve => {
        local.onload = () => resolve();
        local.onerror = () => resolve();
      }),
    );
    local.src = `${window.location.origin}${LOCAL_LOGO}`;
    img.replaceWith(local);
  });

  await Promise.all(loads);
}

function collectBreakpoints(root: HTMLElement, target: number[]) {
  const rootTop = root.getBoundingClientRect().top;

  root.querySelectorAll(BREAK_SELECTOR).forEach(node => {
    const bottom = node.getBoundingClientRect().bottom - rootTop;
    if (bottom > 0) target.push(bottom * SCALE);
  });

  target.sort((a, b) => a - b);
}

// Altura da imagem até o último pixel com conteúdo (+ margem). Evita páginas em branco quando o elemento
// capturado tem espaço vazio no fim (altura mínima, padding).
function contentHeight(canvas: HTMLCanvasElement): number {
  const context = canvas.getContext('2d');
  if (!context) return canvas.height;

  const band = 64;
  for (let bandEnd = canvas.height; bandEnd > 0; bandEnd -= band) {
    const bandStart = Math.max(0, bandEnd - band);
    const { data } = context.getImageData(0, bandStart, canvas.width, bandEnd - bandStart);

    for (let row = bandEnd - bandStart - 1; row >= 0; row -= 1) {
      for (let col = 0; col < canvas.width; col += 2) {
        const index = (row * canvas.width + col) * 4;
        if (data[index] < 245 || data[index + 1] < 245 || data[index + 2] < 245) {
          return Math.min(canvas.height, bandStart + row + 1 + BOTTOM_MARGIN_PX);
        }
      }
    }
  }

  return canvas.height;
}

function addCanvasSlice(
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  start: number,
  end: number,
  mmPerPx: number,
  isFirstPage: boolean,
) {
  const slice = document.createElement('canvas');
  slice.width = canvas.width;
  slice.height = end - start;

  const context = slice.getContext('2d');
  if (!context) throw new Error('Não foi possível preparar a página do PDF');

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, slice.width, slice.height);
  context.drawImage(canvas, 0, start, canvas.width, end - start, 0, 0, canvas.width, end - start);

  if (!isFirstPage) pdf.addPage();
  pdf.addImage(
    slice.toDataURL('image/jpeg', JPEG_QUALITY),
    'JPEG',
    0,
    0,
    canvas.width * mmPerPx,
    (end - start) * mmPerPx,
  );
}

// Gera um PDF A4 a partir do elemento, na mesma escala da impressão (a guia de 110mm continua com 110mm).
export async function exportElementToPdf(element: HTMLElement, { filename }: ExportElementToPdfOptions): Promise<void> {
  if (document.fonts?.ready) await document.fonts.ready;

  const breakpoints: number[] = [];

  const canvas = await html2canvas(element, {
    scale: SCALE,
    useCORS: true,
    windowWidth: Math.max(window.innerWidth, CAPTURE_WINDOW_WIDTH),
    backgroundColor: '#ffffff',
    logging: false,
    onclone: async (clonedDoc, clonedElement) => {
      applyComponentPrintStyles(clonedDoc);
      stretchSvgs(clonedElement);
      await replaceCrossOriginImages(clonedDoc);
      collectBreakpoints(clonedElement, breakpoints);
    },
  });

  // mm por pixel do canvas; se o conteúdo for mais largo que a folha, reduz para caber
  const naturalMmPerPx = PX_TO_MM / SCALE;
  const mmPerPx = Math.min(naturalMmPerPx, A4_WIDTH_MM / canvas.width);
  const pageHeightPx = Math.floor(A4_HEIGHT_MM / mmPerPx);

  const totalHeight = contentHeight(canvas);

  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  let start = 0;
  let isFirstPage = true;

  while (start < totalHeight - 1) {
    let end = Math.min(start + pageHeightPx, totalHeight);

    if (end < totalHeight) {
      // quebra no fim do último bloco de texto que cabe na página (sem deixar a página quase vazia)
      const candidate = [...breakpoints].reverse().find(point => point <= end && point > start + pageHeightPx * 0.6);
      if (candidate) end = Math.floor(candidate);
    }

    addCanvasSlice(pdf, canvas, start, end, mmPerPx, isFirstPage);
    isFirstPage = false;
    start = end;
  }

  pdf.save(filename);
}

// Nome de arquivo sem acentos e caracteres inválidos: "Guia-123-Maria-Silva.pdf"
export function pdfFileName(...parts: Array<string | number | null | undefined>): string {
  const name = parts
    .filter(part => part !== null && part !== undefined && String(part).trim() !== '')
    .map(part =>
      String(part)
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, ''),
    )
    .filter(Boolean)
    .join('-');

  return `${name || 'documento'}.pdf`;
}
